import { useRef } from 'react'
import { useForm } from 'react-hook-form'
import { useState } from 'react'
import StructuredData from '../components/StructuredData'
import './CountertopsLandingPage.css'

interface QuoteFormData {
  name: string
  email: string
  phone: string
  company?: string
  referral?: string
  projectType: string
}

function CountertopsLandingPage() {
  const formRef = useRef<HTMLDivElement>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isSubmitError, setIsSubmitError] = useState(false)
  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
    reset,
  } = useForm<QuoteFormData>()

  const scrollToForm = () => {
    formRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  }

  const FORMSPREE_ENDPOINT = 'https://formspree.io/f/mojnjjob'

  const onSubmit = async (data: QuoteFormData) => {
    try {
      const res = await fetch(FORMSPREE_ENDPOINT, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          name: data.name,
          email: data.email,
          phone: data.phone,
          company: data.company || '',
          referral: data.referral || '',
          projectType: data.projectType,
          _subject: 'Lab Countertops Quote Request',
        }),
      })
      if (!res.ok) throw new Error('Submit failed')
      setIsSubmitted(true)
      reset()
      setTimeout(() => setIsSubmitted(false), 8000)
    } catch {
      setIsSubmitError(true)
      setTimeout(() => setIsSubmitError(false), 5000)
    }
  }

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: 'Laboratory Countertops',
    description:
      'Epoxy, Chemsurf laminate, and Corian lab countertops. Chemical-resistant, fire-rated, and custom-fabricated for labs.',
    brand: { '@type': 'Brand', name: 'Laboratory Specialists' },
    offers: { '@type': 'Offer', priceCurrency: 'USD', availability: 'https://schema.org/UponRequest' },
  }

  return (
    <div className="ctop-landing-page">
      <StructuredData data={structuredData} />

      {/* Hero: headline + sub-headline + proof + CTA */}
      <section className="ctop-hero">
        <div className="ctop-hero-bg" aria-hidden />
        <div className="container ctop-hero-inner">
          <h1 className="ctop-hero-title">
            Lab Countertops That Take the Chemistry—Not the Damage
          </h1>
          <p className="ctop-hero-subtitle">
            Epoxy, Chemsurf laminate, and Corian. We quote the right surface for your lab so you get chemical resistance, compliance, and on-time delivery.
          </p>
          <div className="ctop-hero-cta">
            <button type="button" className="ctop-cta-button" onClick={scrollToForm}>
              Get Your Free Quote
            </button>
            <p className="ctop-hero-cta-note">Custom quote in 24 hours · No obligation</p>
          </div>
        </div>
      </section>

      {/* Three objections (below the fold) */}
      <section className="ctop-objections">
        <div className="container">
          <ul className="ctop-bullets">
            <li>
              <strong>Not sure which material fits your lab?</strong> We'll recommend epoxy, Chemsurf laminate, or Corian based on your chemicals and use.
            </li>
            <li>
              <strong>Worried about lead times or specs?</strong> We source and fabricate to your schedule and handle marine edge, fire-rated, and structural grades.
            </li>
            <li>
              <strong>Need one partner for the whole job?</strong> We do casework, countertops, and install—one quote, one timeline.
            </li>
          </ul>
        </div>
      </section>

      {/* Quote form - primary conversion */}
      <section className="ctop-form-section" ref={formRef} id="quote-form">
        <div className="container">
          <div className="ctop-form-card">
            <h2 className="ctop-form-title">Get Your Custom Quote</h2>
            <p className="ctop-form-lead-magnet">
              Get a custom quote for epoxy, Chemsurf laminate, Corian, and more.
            </p>

            {isSubmitted ? (
              <div className="ctop-form-success">
                <h3>Thanks—we'll be in touch within 24 hours.</h3>
                <p>We'll email or call with your quote and next steps.</p>
              </div>
            ) : (
              <form className="ctop-form" onSubmit={handleSubmit(onSubmit)}>
                {isSubmitError && (
                  <p className="ctop-form-error" role="alert">
                    Something went wrong. Please try again or call us at (541) 658-9374.
                  </p>
                )}
                <div className="ctop-form-group">
                  <label htmlFor="ctop-name">
                    Full Name <span className="ctop-required">*</span>
                  </label>
                  <input
                    type="text"
                    id="ctop-name"
                    {...register('name', {
                      required: 'Name is required',
                      minLength: { value: 2, message: 'Please enter your full name' },
                    })}
                    aria-invalid={errors.name ? 'true' : 'false'}
                    placeholder="First and Last Name"
                  />
                  {errors.name && (
                    <span className="ctop-error" role="alert">
                      {errors.name.message}
                    </span>
                  )}
                </div>

                <div className="ctop-form-group">
                  <label htmlFor="ctop-email">
                    Email <span className="ctop-required">*</span>
                  </label>
                  <input
                    type="email"
                    id="ctop-email"
                    {...register('email', {
                      required: 'Email is required',
                      pattern: {
                        value: /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i,
                        message: 'Please enter a valid email',
                      },
                    })}
                    aria-invalid={errors.email ? 'true' : 'false'}
                    placeholder="Email Address"
                  />
                  {errors.email && (
                    <span className="ctop-error" role="alert">
                      {errors.email.message}
                    </span>
                  )}
                </div>

                <div className="ctop-form-group">
                  <label htmlFor="ctop-phone">
                    Phone <span className="ctop-required">*</span>
                  </label>
                  <input
                    type="tel"
                    id="ctop-phone"
                    {...register('phone', {
                      required: 'Phone is required',
                      pattern: {
                        value: /^[\d\s\-+()]+$/,
                        message: 'Please enter a valid phone number',
                      },
                    })}
                    aria-invalid={errors.phone ? 'true' : 'false'}
                    placeholder="123-456-7890"
                  />
                  {errors.phone && (
                    <span className="ctop-error" role="alert">
                      {errors.phone.message}
                    </span>
                  )}
                </div>

                <div className="ctop-form-group">
                  <label htmlFor="ctop-company">Company / Institution (optional)</label>
                  <input
                    type="text"
                    id="ctop-company"
                    {...register('company')}
                    placeholder="Your lab or company name"
                  />
                </div>

                <div className="ctop-form-group">
                  <label htmlFor="ctop-referral">How did you hear about us? (optional)</label>
                  <input
                    type="text"
                    id="ctop-referral"
                    {...register('referral')}
                    placeholder="e.g. Google, referral, trade show..."
                  />
                </div>

                <div className="ctop-form-group">
                  <label htmlFor="ctop-projectType">
                    What do you need? <span className="ctop-required">*</span>
                  </label>
                  <select
                    id="ctop-projectType"
                    {...register('projectType', { required: 'Please select an option' })}
                    aria-invalid={errors.projectType ? 'true' : 'false'}
                  >
                    <option value="">Select...</option>
                    <option value="new-lab">New lab countertops</option>
                    <option value="replacement">Replacement / retrofit</option>
                    <option value="materials-only">Materials quote only</option>
                    <option value="not-sure">Not sure yet—need guidance</option>
                  </select>
                  {errors.projectType && (
                    <span className="ctop-error" role="alert">
                      {errors.projectType.message}
                    </span>
                  )}
                </div>

                <p className="ctop-privacy">
                  By submitting you agree to our{' '}
                  <a href="/privacy" target="_blank" rel="noopener noreferrer">
                    Privacy Policy
                  </a>
                  . We won't share your information.
                </p>

                <button type="submit" className="ctop-submit" disabled={isSubmitting}>
                  {isSubmitting ? 'Sending...' : 'Get My Quote'}
                </button>
              </form>
            )}
          </div>
        </div>
      </section>

      {/* Legal */}
      <section className="ctop-legal">
        <div className="container">
          <p className="ctop-legal-text">
            Laboratory Specialists. Lab equipment installation and materials. For hydrofluoric acid (HF) or other specialty applications, we can recommend appropriate options (e.g. polypropylene).{' '}
            <a href="/privacy">Privacy Policy</a> · <a href="/contact">Contact</a>
          </p>
        </div>
      </section>
    </div>
  )
}

export default CountertopsLandingPage
